import { useEffect, useState } from 'react';
import TileCard from './TileCard';
import { getCachedResponse, getServiceBusSummary, postResubmitDlq } from '../api/client';
import type { Env, ServiceBusEntityRow, ServiceBusSummary } from '../types';

const HELP =
  'Resubmit dead-lettered messages back to their source topic/queue. Only entities ' +
  'with DLQ > 0 can be actioned. Per-click default is 10 messages (cap 100). Every ' +
  'resubmit is audited to App Insights with the authenticated user.';

const MAX_PER_CLICK = 100;
const DEFAULT_PER_CLICK = 10;

interface Props {
  env: Env;
  refreshToken?: number;
  onResubmitted?: () => void;
}

interface RowState {
  input: number;
  running: boolean;
  result: string | null;
  error: string | null;
}

function entityArgs(r: ServiceBusEntityRow): { topic?: string; subscription?: string; queue?: string } {
  if (r.kind === 'queue') return { queue: r.name };
  if (r.kind === 'subscription' && r.topic) return { topic: r.topic, subscription: r.name };
  return {};
}

function entityLabel(r: ServiceBusEntityRow): string {
  if (r.kind === 'subscription' && r.topic) return `${r.topic}/${r.name}`;
  return r.name;
}

export default function DlqActionsPanel({ env, refreshToken = 0, onResubmitted }: Props) {
  const [data, setData] = useState<ServiceBusSummary | null>(() =>
    getCachedResponse<ServiceBusSummary>('service-bus-summary', { env }),
  );
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedResponse<ServiceBusSummary>('service-bus-summary', { env });
    if (cached) setData(cached);
    setLoading(cached === null);
    setError(null);
    getServiceBusSummary(env)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, refreshToken]);

  // Actionable rows = leaf entities (queues + subscriptions, never topics —
  // topics are an aggregate of their subscriptions) where DLQ > 0.
  const actionable: ServiceBusEntityRow[] =
    data?.rows?.filter((r) => r.kind !== 'topic' && r.dlq > 0) ?? [];

  function updateRow(key: string, patch: Partial<RowState>) {
    setRowState((s) => {
      const existing: RowState = s[key] ?? {
        input: DEFAULT_PER_CLICK,
        running: false,
        result: null,
        error: null,
      };
      return { ...s, [key]: { ...existing, ...patch } };
    });
  }

  async function resubmit(r: ServiceBusEntityRow) {
    const key = entityLabel(r);
    const st = rowState[key] ?? { input: DEFAULT_PER_CLICK, running: false, result: null, error: null };
    const count = Math.min(Math.max(Math.trunc(st.input || DEFAULT_PER_CLICK), 1), MAX_PER_CLICK);
    const confirmed = window.confirm(
      `Resubmit ${count} dead-lettered message${count === 1 ? '' : 's'} on ${key}?\n\n` +
        `They will be sent back to the source topic/queue and the original DLQ entries completed.`,
    );
    if (!confirmed) return;

    updateRow(key, { running: true, result: null, error: null });
    try {
      const res = await postResubmitDlq({ env, ...entityArgs(r), maxMessages: count });
      updateRow(key, {
        running: false,
        result: `Resubmitted ${res.resubmitted} · Abandoned ${res.abandoned}`,
      });
      onResubmitted?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      updateRow(key, { running: false, error: msg });
    }
  }

  const generatedFooter = data ? <span>As of {new Date(data.generatedAt).toLocaleTimeString()}</span> : undefined;

  return (
    <TileCard
      title={`DLQ reconciliation — ${env.toUpperCase()}`}
      help={HELP}
      loading={loading}
      error={error}
      footer={generatedFooter}
    >
      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {data?.status === 'ok' && actionable.length === 0 && (
        <p className="tile-empty">No entities have dead-lettered messages right now.</p>
      )}
      {data?.status === 'ok' && actionable.length > 0 && (
        <div className="workflow-table-wrap">
          <table className="workflow-table">
            <thead>
              <tr>
                <th>Entity</th>
                <th className="num">DLQ</th>
                <th className="num">Count</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {actionable.map((r) => {
                const key = entityLabel(r);
                const st = rowState[key] ?? { input: DEFAULT_PER_CLICK, running: false, result: null, error: null };
                return (
                  <tr key={key}>
                    <td>{key}</td>
                    <td className="num bad">{r.dlq}</td>
                    <td className="num">
                      <input
                        type="number"
                        min={1}
                        max={MAX_PER_CLICK}
                        step={1}
                        value={st.input}
                        onChange={(e) => updateRow(key, { input: Number(e.target.value) })}
                        disabled={st.running}
                        className="dlq-count-input"
                        aria-label={`Messages to resubmit for ${key}`}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => resubmit(r)}
                        disabled={st.running}
                        className="dlq-resubmit-btn"
                      >
                        {st.running ? 'Resubmitting…' : 'Resubmit'}
                      </button>
                      {st.result && <div className="dlq-result-ok">{st.result}</div>}
                      {st.error && <div className="dlq-result-err">Failed: {st.error}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </TileCard>
  );
}
